"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Table = void 0;
const lit_1 = require("lit");
const decorators_js_1 = require("lit/decorators.js");
const class_map_js_1 = require("lit/directives/class-map.js");
const if_defined_js_1 = require("lit/directives/if-defined.js");
const style_map_js_1 = require("lit/directives/style-map.js");
const icon_names_1 = require("@/components/common/icon/icon-names");
const shared_styles_1 = require("@/stylesheets/shared.styles");
require("@/components/common/icon");
let Table = class Table extends lit_1.LitElement {
    constructor() {
        super(...arguments);
        this.caption = '';
        this.rows = lit_1.nothing;
        this.columns = [];
    }
    render() {
        return (0, lit_1.html) `
      <table style=${(0, style_map_js_1.styleMap)(this.getTableStyle())}>
        <caption hidden>${this.caption}</caption>
        <colgroup>${this.renderColumns()}</colgroup>
        <thead>
          <tr>${this.renderHeaders()}</tr>
        </thead>
        <tbody>${this.rows}</tbody>
      </table>
    `;
    }
    // 임의 값이라 나열할 수 없고 host가 소비하므로 host에 직접 주입한다.
    updated(changedProperties) {
        if (!changedProperties.has('height'))
            return;
        if (!this.height) {
            this.style.removeProperty('--table-height');
            return;
        }
        this.style.setProperty('--table-height', this.height);
    }
    // 너비를 준 열은 그만큼만 차지하고, 너비 없는 마지막 열이 남는 폭을 채운다.
    // 마지막 열이 0으로 무너지지 않도록 고정 열 폭의 합에 최소 폭을 더해 그 아래에서는 가로 스크롤로 넘긴다.
    getTableStyle() {
        const fixedWidths = this.columns.map(column => column.width).filter(Boolean);
        const lastColumn = this.columns[this.columns.length - 1];
        if (!lastColumn || lastColumn.width || fixedWidths.length === 0)
            return {};
        return {
            width: '100%',
            minWidth: `calc(${fixedWidths.join(' + ')} + var(--size-80) * 2)`,
        };
    }
    renderColumns() {
        return this.columns.map(column => this.renderColumn(column));
    }
    renderHeaders() {
        return this.columns.map(column => this.renderHeader(column));
    }
    renderHeader(column) {
        return (0, lit_1.html) `
      <th
        class=${(0, class_map_js_1.classMap)({
            'align-center': column.textAlign === 'center',
            'align-right': column.textAlign === 'right',
        })}
        scope="col"
        aria-sort=${(0, if_defined_js_1.ifDefined)(column.sortable ? 'none' : undefined)}
      >
        ${this.renderHeaderContent(column)}
      </th>
    `;
    }
    renderHeaderContent(column) {
        if (!column.sortable)
            return column.label;
        return (0, lit_1.html) `
      <span
        class="header-label"
        style=${(0, style_map_js_1.styleMap)({ justifyContent: this.getHeaderJustifyContent(column) })}
      >
        ${column.label}
        <mm-icon name=${icon_names_1.ICON_NAMES.SORT} size="tiny"></mm-icon>
      </span>
    `;
    }
    renderColumn(column) {
        return (0, lit_1.html) `
      <col style=${(0, style_map_js_1.styleMap)(column.width ? { width: column.width } : {})} />
    `;
    }
    getHeaderJustifyContent(column) {
        if (column.textAlign === 'center')
            return 'center';
        if (column.textAlign === 'right')
            return 'flex-end';
        return 'flex-start';
    }
};
Table.styles = [
    shared_styles_1.resetStyles,
    (0, lit_1.css) `
      :host {
        --table-width: 100%;
        --table-row-border: var(--border);
        --table-cell-padding: var(--space-2) 0;
        --table-cell-padding-inline: var(--space-2);
        --table-cell-background-color: var(--background-color);
        --table-checkbox-column-width: var(--size-32);

        display: block;
        width: var(--table-width);
        height: var(--table-height);
        background-color: var(--background-subtle-color);
        overflow-x: auto;
      }

      .header-label {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      table {
        border-collapse: collapse;
        border-spacing: 0;
        border-left-style: hidden;
        border-right-style: hidden;
        font-size: inherit;

        width: max-content;
        min-width: 100%;
        table-layout: fixed;

        background: var(--table-cell-background-color);
      }
      table caption {
        padding: var(--space-2) 0;
        font-size: var(--font-size-12);
        text-align: left;
      }
      table thead tr th {
        padding: var(--space-1) 0;
        padding-inline: var(--table-cell-padding-inline);
        font-size: var(--font-size-12);
        color: var(--foreground-subtle-color);
        text-align: left;
      }
      table tbody tr {
        border-bottom: var(--table-row-border);
        background: var(--table-cell-background-color);
        position: relative;
      }
      table tbody tr:hover {
        --table-cell-background-color: var(--interaction-hover-background-color);
      }
      table tbody tr th {
        padding-inline: var(--table-cell-padding-inline);
        text-align: left;
      }
      table tbody tr td {
        padding: var(--table-cell-padding);
        padding-inline: var(--table-cell-padding-inline);
      }
      table .table-cell-ellipsis {
        flex: 1;
        width: 0;
        min-width: 0;
        max-width: 240px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      table .cell-align-right {
        text-align: right;
      }
      table tbody[role='rowgroup'] tr th {
        width: 25%;
      }
      table tbody[role='rowgroup'] tr th + th {
        width: auto;
      }

      th.align-center {
        text-align: center;
      }

      th.align-right {
        text-align: right;
      }
    `,
];
__decorate([
    (0, decorators_js_1.property)({ type: String })
], Table.prototype, "caption", void 0);
__decorate([
    (0, decorators_js_1.property)({ attribute: false })
], Table.prototype, "rows", void 0);
__decorate([
    (0, decorators_js_1.property)({ attribute: false })
], Table.prototype, "columns", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], Table.prototype, "height", void 0);
Table = __decorate([
    (0, decorators_js_1.customElement)('mm-table')
], Table);
exports.Table = Table;
//# sourceMappingURL=data:application/json;base64,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